// src/pages/CountryPage.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import './css/CountryPage.css';
import * as d3 from 'd3';

function CountryPage() {
    const { countryCode } = useParams();
    const [countryData, setCountryData] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasChartData, setHasChartData] = useState(true);

    useEffect(() => {
        const fetchCountryData = async () => {
            setLoading(true);
            try {
                // Fetch country data
                const response = await fetch(`https://restcountries.com/v3.1/alpha/${countryCode}`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                if (data && data.length > 0) {
                    setCountryData(data[0]);
                } else {
                    setError('No data found for this country');
                }

                // Fetch and process CSV data
                const csvResponse = await fetch(`${process.env.PUBLIC_URL}/data/tourist-departures-per-1000-vs-gdp.csv`);
                const csvText = await csvResponse.text();
                const csvRows = csvText.split('\n').slice(1); // Skip header
                const filteredData = csvRows
                    .map(row => {
                        const [entity, code, year, departures, gdp] = row.split(',');
                        return { code, year: +year, departures: parseFloat(departures), gdp: parseFloat(gdp) };
                    })
                    .filter(row => row.code === countryCode && row.departures && row.gdp)
                    .sort((a, b) => a.gdp - b.gdp);

                setChartData(filteredData);
                setHasChartData(filteredData.length > 0);
            } catch (err) {
                setError('Failed to fetch data');
            } finally {
                setLoading(false);
            }
        };

        fetchCountryData();
    }, [countryCode]);

    useEffect(() => {
        if (chartData.length > 0) {
            drawChart(chartData);
        }
    }, [chartData]);

    const drawChart = (data) => {
        // Set dimensions and margins for the chart
        const margin = { top: 20, right: 30, bottom: 50, left: 60 };
        const width = 600 - margin.left - margin.right;
        const height = 400 - margin.top - margin.bottom;
    
        // Clear any existing SVG elements
        d3.select("#chart").select("svg").remove();
    
        // Create SVG container
        const svg = d3.select("#chart")
            .append("svg")
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom)
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);
    
        // Set up scales
        const xScale = d3.scaleLinear()
            .domain(d3.extent(data, d => d.gdp))
            .range([0, width]);
    
        const yScale = d3.scaleLinear()
            .domain([0, d3.max(data, d => d.departures)])
            .range([height, 0]);
    
        // Add X axis
        svg.append("g")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(xScale).ticks(5).tickFormat(d3.format(".0f")))
            .append("text")
            .attr("fill", "#000")
            .attr("x", width / 2)
            .attr("y", 40)
            .attr("text-anchor", "middle")
            .text("GDP per Capita (constant 2017 international $)");
    
        // Add Y axis
        svg.append("g")
            .call(d3.axisLeft(yScale).ticks(5))
            .append("text")
            .attr("fill", "#000")
            .attr("transform", "rotate(-90)")
            .attr("y", -50)
            .attr("x", -height / 2)
            .attr("text-anchor", "middle")
            .text("International Tourist Departures per 1,000 people");
    
        // Create a tooltip div
        const tooltip = d3.select("#chart")
            .append("div")
            .attr("class", "tooltip")
            .style("opacity", 0);
    
        // Add scatter plot points with hover interactivity
        svg.selectAll("circle")
            .data(data)
            .enter()
            .append("circle")
            .attr("cx", d => xScale(d.gdp))
            .attr("cy", d => yScale(d.departures))
            .attr("r", 5)
            .attr("fill", "steelblue")
            .attr("stroke", "white")
            .attr("stroke-width", 1)
            .on("mouseover", function(event, d) {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr("r", 8)
                    .attr("fill", "orange");
    
                tooltip.transition()
                    .duration(200)
                    .style("opacity", .9);
                tooltip.html(`Year: ${d.year}<br/>GDP: ${d.gdp.toFixed(2)}<br/>Departures: ${d.departures.toFixed(2)}`)
                    .style("left", (event.pageX + 10) + "px")
                    .style("top", (event.pageY - 28) + "px");
            })
            .on("mouseout", function() {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr("r", 5)
                    .attr("fill", "steelblue");
    
                tooltip.transition()
                    .duration(500)
                    .style("opacity", 0);
            });
    };

    if (loading) return (
        <div className="loader-container">
            <CircularProgress />
        </div>
    );
    if (error) return <div className="error-message">{error}</div>;

    if (!countryData) return null;

    const { currencies, capital, languages, flags, population, timezones } = countryData;

    return (
        <>
            <div className="country-page">
                <div className="flag-container">
                    <img src={flags.svg} alt={`Flag of ${countryData.name.common}`} className="country-flag" />
                </div>
                <div className="info-container">
                    <h1 className="country-name">{countryData.name.common}</h1>
                    <ul>
                        <li><strong>Capital:</strong> {capital ? capital.join(', ') : 'N/A'}</li>
                        <li><strong>Population:</strong> {population.toLocaleString()}</li>
                        <li><strong>Currencies:</strong> {currencies ? Object.values(currencies).map(currency => `${currency.name} (${currency.symbol})`).join(', ') : 'N/A'}</li>
                        <li><strong>Languages:</strong> {languages ? Object.values(languages).join(', ') : 'N/A'}</li>
                        <li><strong>Timezones:</strong> {timezones ? timezones.join(', ') : 'N/A'}</li>
                    </ul>
                </div>
            </div>
            
            {/* Chart Container */}
            <div className="chart-container">
                <h2>International Tourist Departures vs GDP per Capita</h2>
                {hasChartData ? (
                    <div id="chart"></div>
                ) : (
                    <div className="no-data-message">No chart data available for this country</div>
                )}
            </div>
        </>
    );
}

export default CountryPage;